import { forwardRef, useState, useEffect } from 'react';

// material-ui
import {
    Button,
    Dialog,
    FormControl,
    FormHelperText,
    IconButton,
    Grid,
    Stack,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Zoom,
    DialogContent
} from '@mui/material';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import validationSchema from 'utils/schema/BoardSchema';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';

// third-party
import { useFormik } from 'formik';

const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const AddBoard = ({ selectedBoard, open, handleClose, setReload }) => {

    const [venueArray, setVenuArray] = useState([]);
    const [turfArray, setTurfArray] = useState([]);

    const submitHandler = () => {
        if (selectedBoard) {
            updateBoardByUuidApi.execute(formik.values, selectedBoard.uuid);
        } else {
            createBoardApi.execute(formik.values);
        }
    };

    const closeDialog = () => {
        formik.resetForm();
        handleClose();
    };

    const createSuccessHandler = () => {
        formik.setSubmitting(true);
        formik.resetForm();
        setReload(r => !r);
        handleClose();
    };

    const getVenueSuccessHandler = (response) => {
        setVenuArray(response.data);
    };

    const getTurfSuccessHandler = (response) => {
        setTurfArray(response.data);
    };

    const createBoardApi = useApi(VenueService.createBoard, createSuccessHandler, true);
    const updateBoardByUuidApi = useApi(VenueService.updateBoardByUuid, createSuccessHandler, true);
    const getVenueListApi = useApi(VenueService.getVenueDropdownList, getVenueSuccessHandler);
    const getTurfListApi = useApi(VenueService.getTurfDropdownList, getTurfSuccessHandler);

    const formik = useFormik({
        initialValues: {
            uuid_venue: selectedBoard ? selectedBoard.uuid_venue : '',
            uuid_turf: selectedBoard ? selectedBoard.uuid_turf : '',
            name: selectedBoard ? selectedBoard.name : '',
            holiday_price: selectedBoard ? parseFloat(selectedBoard.holiday_price) : '',
            weekday_price: selectedBoard ? parseFloat(selectedBoard.weekday_price) : '',
            weekend_price: selectedBoard ? parseFloat(selectedBoard.weekend_price) : '',
            discount_percentage: selectedBoard ? parseFloat(selectedBoard.discount_percentage) : 0,
            board_width: selectedBoard ? parseFloat(selectedBoard.board_width) : '',
            board_height: selectedBoard ? parseFloat(selectedBoard.board_height) : ''
        },
        validationSchema,
        onSubmit: submitHandler
    });

    useEffect(() => {
        if (selectedBoard) {
            formik.setFieldValue('uuid_venue', selectedBoard.uuid_venue);
            formik.setFieldValue('uuid_turf', selectedBoard.uuid_turf);
            formik.setFieldValue('name', selectedBoard.name);
            formik.setFieldValue('holiday_price', parseFloat(selectedBoard.holiday_price));
            formik.setFieldValue('weekday_price', parseFloat(selectedBoard.weekday_price));
            formik.setFieldValue('weekend_price', parseFloat(selectedBoard.weekend_price));
            formik.setFieldValue('discount_percentage', parseFloat(selectedBoard.discount_percentage) ? parseFloat(selectedBoard.discount_percentage) : 0);
            formik.setFieldValue('board_width', parseFloat(selectedBoard.board_width) ? parseFloat(selectedBoard.board_width) : '');
            formik.setFieldValue('board_height', parseFloat(selectedBoard.board_height) ? parseFloat(selectedBoard.board_height) : '');
        }
    }, [selectedBoard]);

    useEffect(() => {
        if (formik.values.uuid_venue) {
            getTurfListApi.execute({ venueUuid: formik.values.uuid_venue });
        }
    }, [formik.values.uuid_venue]);

    useEffect(() => {
        getVenueListApi.execute();
    }, []);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={closeDialog}
            fullWidth
            maxWidth="md"
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogContent sx={{ p: 0 }}>
                <MainCard
                    title={selectedBoard ? 'Edit Board Details' : 'Add Board Details'}
                    secondary={
                        <IconButton onClick={closeDialog} size="large" aria-label="Close Edit Board">
                            <HighlightOffTwoToneIcon fontSize="small" />
                        </IconButton>
                    }
                    sx={{ border: 0 }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <InputLabel id="venue-select-label">Venue*</InputLabel>
                                    <Select
                                        labelId="venue-select-label"
                                        id="venue-select"
                                        label="Venue*"
                                        name="uuid_venue"
                                        value={formik.values.uuid_venue}
                                        onChange={formik.handleChange}
                                        error={formik.touched.uuid_venue && Boolean(formik.errors.uuid_venue)}
                                    >
                                        {
                                            venueArray.map((venueObj, index) => {
                                                return (
                                                    <MenuItem key={index} value={venueObj.uuid}>{venueObj.venue_name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.uuid_venue && formik.errors.uuid_venue && (
                                    <FormHelperText error id="standard-weight-helper-select-uuid_venue-board">
                                        {formik.errors.uuid_venue}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <InputLabel id="turf-select-label">Court*</InputLabel>
                                    <Select
                                        labelId="turf-select-label"
                                        id="turf-select"
                                        label="Turf*"
                                        name="uuid_turf"
                                        value={formik.values.uuid_turf}
                                        onChange={formik.handleChange}
                                        error={formik.touched.uuid_turf && Boolean(formik.errors.uuid_turf)}
                                    >
                                        {
                                            turfArray.map((turfObj, index) => {
                                                return (
                                                    <MenuItem key={index} value={turfObj.uuid}>{turfObj.turf_name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.uuid_turf && formik.errors.uuid_turf && (
                                    <FormHelperText error id="standard-weight-helper-select-uuid_turf-board">
                                        {formik.errors.uuid_turf}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="name"
                                        label="Board Name*"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        error={formik.touched.name && Boolean(formik.errors.name)}
                                    />
                                </FormControl>
                                {formik.touched.name && formik.errors.name && (
                                    <FormHelperText error id="standard-weight-helper-text-name-board">
                                        {formik.errors.name}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="weekday_price"
                                        label="Weekday Price*"
                                        value={formik.values.weekday_price}
                                        onChange={formik.handleChange}
                                        error={formik.touched.weekday_price && Boolean(formik.errors.weekday_price)}
                                    />
                                </FormControl>
                                {formik.touched.weekday_price && formik.errors.weekday_price && (
                                    <FormHelperText error id="standard-weight-helper-text-weekday-board">
                                        {formik.errors.weekday_price}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="weekend_price"
                                        label="Weekend Price*"
                                        value={formik.values.weekend_price}
                                        onChange={formik.handleChange}
                                        error={formik.touched.weekend_price && Boolean(formik.errors.weekend_price)}
                                    />
                                </FormControl>
                                {formik.touched.weekend_price && formik.errors.weekend_price && (
                                    <FormHelperText error id="standard-weight-helper-text-weekend-board">
                                        {formik.errors.weekend_price}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="holiday_price"
                                        label="Holiday Price*"
                                        value={formik.values.holiday_price}
                                        onChange={formik.handleChange}
                                        error={formik.touched.holiday_price && Boolean(formik.errors.holiday_price)}
                                    />
                                </FormControl>
                                {formik.touched.holiday_price && formik.errors.holiday_price && (
                                    <FormHelperText error id="standard-weight-helper-text-holiday-board">
                                        {formik.errors.holiday_price}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="discount_percentage"
                                        label="Discount Percentage*"
                                        value={formik.values.discount_percentage}
                                        onChange={formik.handleChange}
                                        error={formik.touched.discount_percentage && Boolean(formik.errors.discount_percentage)}
                                    />
                                </FormControl>
                                {formik.touched.discount_percentage && formik.errors.discount_percentage && (
                                    <FormHelperText error id="standard-weight-helper-text-discount-percentage-board">
                                        {formik.errors.discount_percentage}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="board_width"
                                        label="Board Width*"
                                        value={formik.values.board_width}
                                        onChange={formik.handleChange}
                                        error={formik.touched.board_width && Boolean(formik.errors.board_width)}
                                    />
                                </FormControl>
                                {formik.touched.board_width && formik.errors.board_width && (
                                    <FormHelperText error id="standard-weight-helper-text-board-width-board">
                                        {formik.errors.board_width}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="medium">
                                    <TextField
                                        fullWidth
                                        name="board_height"
                                        label="Board Height*"
                                        value={formik.values.board_height}
                                        onChange={formik.handleChange}
                                        error={formik.touched.board_height && Boolean(formik.errors.board_height)}
                                    />
                                </FormControl>
                                {formik.touched.board_height && formik.errors.board_height && (
                                    <FormHelperText error id="standard-weight-helper-text-board-height-board">
                                        {formik.errors.board_height}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                        color="error"
                                        onClick={closeDialog}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={formik.handleSubmit}
                                    >
                                        {selectedBoard ? 'Update' : 'Create'} Board
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                </MainCard>
            </DialogContent>
        </Dialog>
    );
};

export default AddBoard;
